import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { billingApi } from '../api/services';
import type { BillingProduct } from '../types/models';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// SignupPage — Stratium-branded self-service signup. The flow is:
//
//   1. User picks a plan + seat count and supplies their contact
//      details (full name, work email, company).
//   2. Submit POSTs to `/api/billing/checkout` which creates a Stripe
//      Checkout Session (with a 14-day trial) and returns `checkoutUrl`.
//   3. We redirect the browser to Stripe Checkout, where the user
//      enters their card details.
//   4. On successful payment Stripe fires `checkout.session.completed`
//      to `/api/billing/webhook`, which provisions the tenant, the
//      owner user, seeds defaults, and emails the user a "set your
//      password" link.
//   5. Stripe's success_url drops the user on `/onboarding/success`
//      with the session id, where they're told to check their email.
//
// We don't collect a password here — the password is set via the
// emailed reset link after the webhook lands. We also don't apply
// any tokens; the user signs in once their account is provisioned
// and they've set their password.
// ---------------------------------------------------------------------------

// Shared dark backdrop tokens — match LoginPage so the two pages
// read as a set. Solid base + warm copper halo bottom-centre + cool
// blue top-glow + vignette.
function isWorkEmailish(email: string): boolean {
  // Cheap signal — full validation happens server-side. We just want
  // to catch the obvious gmail/yahoo/outlook personal addresses with
  // a calm inline note rather than a server roundtrip.
  const PERSONAL = ['gmail.com', 'googlemail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 'icloud.com', 'me.com', 'aol.com'];
  const dom = email.split('@')[1]?.toLowerCase() ?? '';
  return !!dom && !PERSONAL.includes(dom);
}

function formatPriceForPlan(p: BillingProduct): string {
  if (p.isCustomPricing) return 'Custom';
  const pounds = (p.pricePerSeatPence / 100).toFixed(p.pricePerSeatPence % 100 === 0 ? 0 : 2);
  const symbol = p.currency?.toLowerCase() === 'gbp' ? '£' : p.currency?.toLowerCase() === 'usd' ? '$' : '';
  return `${symbol}${pounds}`;
}

const FIELD_CLASS =
  'h-12 rounded-[11px] border border-[rgba(234,238,246,0.12)] bg-[rgba(234,238,246,0.04)] px-[15px] py-[13px] text-[15px] text-[#EAEEF6] placeholder:text-[rgba(234,238,246,0.30)] focus-visible:border-[#7DB1FF] focus-visible:ring-[3px] focus-visible:ring-[rgba(125,177,255,0.16)]';

const PRIMARY_BUTTON =
  'h-12 w-full rounded-[11px] border-0 bg-[#EAEEF6] text-[15px] font-semibold text-[#070A12] transition-transform duration-150 hover:-translate-y-px hover:bg-[#EAEEF6] hover:shadow-[0_10px_32px_rgba(125,177,255,0.22)] disabled:opacity-60 disabled:hover:translate-y-0';

export default function SignupPage() {
  const [searchParams] = useSearchParams();

  const [plans, setPlans] = useState<BillingProduct[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [seatCount, setSeatCount] = useState<number>(1);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailHint, setEmailHint] = useState('');

  // Document title sets a clean "Start your trial · Cadence" while the
  // surface is mounted; restore on unmount.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Start your trial · Cadence';
    return () => { document.title = prev; };
  }, []);

  // Load available plans on mount. Public endpoint, cached server-side
  // for 5 minutes so this is cheap. Preselect the IsPopular plan, or
  // the first selectable (non-custom) plan if none are flagged.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await billingApi.getPlans();
        if (cancelled) return;
        const active = data.filter(p => !p.isCustomPricing);
        setPlans(active);
        const popular = active.find(p => p.isPopular);
        setSelectedPlanId(popular?.id ?? active[0]?.id ?? '');
      } catch (err: any) {
        if (!cancelled) {
          setPlansError(err?.response?.data?.error ?? 'Could not load plans. Please try again.');
        }
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Prefill email if it was passed in the URL (deep-link from the
  // marketing hero "Start free trial" button).
  useEffect(() => {
    const e = searchParams.get('email');
    if (e) setEmail(e);
  }, [searchParams]);

  const selectedPlan = useMemo(
    () => plans.find(p => p.id === selectedPlanId) ?? null,
    [plans, selectedPlanId],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim()) { setError('Please enter your full name.'); return; }
    if (!email.trim()) { setError('Please enter your work email.'); return; }
    if (!selectedPlan) { setError('Please choose a plan to continue.'); return; }
    setSubmitting(true);
    try {
      const { checkoutUrl } = await billingApi.checkout({
        productId: selectedPlan.id,
        email: email.trim(),
        organisationName: company.trim() || `${fullName.trim()}'s team`,
        executiveName: fullName.trim(),
        seatCount: Math.max(1, Math.min(seatCount, selectedPlan.maxExecutiveSeats)),
      });
      // Hand the user to Stripe — Stripe will redirect to the success
      // page (which is on this origin) once payment is captured, and
      // the webhook will have provisioned the tenant by then.
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="dark relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070A12]">
      {/* Ambient backdrop — copper halo bottom-centre + cool blue
          top-glow + vignette. Mirrors Login so the auth pair reads as
          one moment. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute left-1/2 bottom-[-24%] h-[78vh] w-[120vw] -translate-x-1/2 rounded-full blur-[14px]"
          style={{ background: 'radial-gradient(ellipse at center, rgba(255,176,122,0.14) 0%, transparent 62%)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 90% at 50% -10%, rgba(125,177,255,0.05), transparent 55%), linear-gradient(180deg, rgba(0,0,0,0.28), transparent 32%, transparent 68%, rgba(0,0,0,0.45))',
          }}
        />
      </div>

      <div className="relative z-[2] mx-4 my-10 flex w-full max-w-[440px] flex-col p-[32px]">
        <div className="mb-5">
          <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
        </div>

        <h1 className="mb-[10px] font-serif text-[33px] font-light leading-[1.12] tracking-[-0.01em] text-white">
          Start your 14&#8209;day trial.
        </h1>
        <p className="mb-[30px] text-[15px] font-light tracking-wide text-[rgba(234,238,246,0.62)]">
          Pick a plan to begin. No charges during the trial.
        </p>

        {error && (
          <Alert variant="error" className="mb-3 rounded-xl">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Plan picker — selectable cards. Pre-loaded from the platform
            admin's BillingProduct table; selecting one drives which
            Stripe Price the checkout session uses. */}
        <div className="mb-5">
          <Label className="mb-[7px] block text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
            Choose a plan
          </Label>
          {plansLoading ? (
            <div className="flex h-[100px] items-center justify-center rounded-[11px] border border-[rgba(234,238,246,0.08)] bg-[rgba(234,238,246,0.02)]">
              <Loader2 className="h-5 w-5 animate-spin text-[rgba(234,238,246,0.40)]" />
            </div>
          ) : plansError ? (
            <Alert variant="error" className="rounded-xl">
              <AlertDescription>{plansError}</AlertDescription>
            </Alert>
          ) : plans.length === 0 ? (
            <Alert variant="warning" className="rounded-xl">
              <AlertDescription>
                No plans are available right now. Please contact{' '}
                <a href="mailto:sales@stratiumhq.com" className="underline">sales@stratiumhq.com</a>.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col gap-2">
              {plans.map(p => {
                const active = p.id === selectedPlanId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedPlanId(p.id)}
                    aria-pressed={active}
                    className={cn(
                      'flex items-start gap-3 rounded-[11px] border px-4 py-3 text-left transition-colors',
                      active
                        ? 'border-[#7DB1FF] bg-[rgba(125,177,255,0.06)]'
                        : 'border-[rgba(234,238,246,0.12)] bg-[rgba(234,238,246,0.02)] hover:border-[rgba(234,238,246,0.25)]',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1 grid h-4 w-4 shrink-0 place-items-center rounded-full border-2',
                        active ? 'border-[#7DB1FF] bg-[#7DB1FF]' : 'border-[rgba(234,238,246,0.30)]',
                      )}
                    >
                      {active && <Check className="h-2.5 w-2.5 text-[#070A12]" strokeWidth={3} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[15px] font-semibold text-white">
                          {p.name}
                          {p.isPopular && (
                            <span className="ml-2 rounded-full bg-[rgba(125,177,255,0.16)] px-2 py-[1px] text-[10px] font-medium uppercase tracking-wide text-[#7DB1FF]">
                              Popular
                            </span>
                          )}
                        </span>
                        <span className="text-[14px] font-medium text-[rgba(234,238,246,0.78)]">
                          {formatPriceForPlan(p)}
                          <span className="text-[12px] font-normal text-[rgba(234,238,246,0.40)]">
                            {' '}/ seat / {p.billingInterval}
                          </span>
                        </span>
                      </div>
                      {p.description && (
                        <p className="mt-1 text-[12.5px] leading-snug text-[rgba(234,238,246,0.60)]">
                          {p.description}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-[14px]">
          <div className="flex flex-col gap-[7px]">
            <Label htmlFor="signup-name" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
              Full name
            </Label>
            <Input
              id="signup-name"
              type="text"
              autoComplete="name"
              required
              autoFocus
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Alex Morgan"
              className={FIELD_CLASS}
            />
          </div>

          <div className="flex flex-col gap-[7px]">
            <Label htmlFor="signup-email" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
              Work email
            </Label>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailHint(''); }}
              onBlur={() => {
                if (email && !isWorkEmailish(email)) {
                  setEmailHint('A work email helps us connect your calendar properly.');
                }
              }}
              placeholder="you@company.com"
              className={FIELD_CLASS}
              aria-describedby={emailHint ? 'signup-email-hint' : undefined}
            />
            {emailHint && (
              <p id="signup-email-hint" className="text-[11.5px] text-[#E0685D]/80">
                {emailHint}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-[7px]">
            <Label htmlFor="signup-company" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
              Company <span className="text-[rgba(234,238,246,0.30)]">(optional)</span>
            </Label>
            <Input
              id="signup-company"
              type="text"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Company name"
              className={FIELD_CLASS}
            />
          </div>

          {selectedPlan && !selectedPlan.isCustomPricing && (
            <div className="flex flex-col gap-[7px]">
              <Label htmlFor="signup-seats" className="text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]">
                Executive seats
              </Label>
              <Input
                id="signup-seats"
                type="number"
                min={1}
                max={selectedPlan.maxExecutiveSeats}
                value={seatCount}
                onChange={(e) => setSeatCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className={FIELD_CLASS}
              />
              <p className="text-[11.5px] text-[rgba(234,238,246,0.40)]">
                Up to {selectedPlan.maxExecutiveSeats} seats on {selectedPlan.name}. You won't be charged until your trial ends.
              </p>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting || plansLoading || !selectedPlan}
            className={cn('mt-[6px] gap-2', PRIMARY_BUTTON)}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Continue to payment
          </Button>
        </form>

        <p className="mt-5 text-center text-[12px] leading-relaxed text-[rgba(234,238,246,0.40)]">
          By starting your trial you agree to our{' '}
          <a href="https://stratiumhq.com/terms" target="_blank" rel="noopener noreferrer" className="text-[rgba(234,238,246,0.62)] hover:text-white">Terms</a>
          {' '}and{' '}
          <a href="https://stratiumhq.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[rgba(234,238,246,0.62)] hover:text-white">Privacy Policy</a>.
          You will not be charged during your 14-day trial.
        </p>

        <p className="mt-6 text-center text-[13px] text-[rgba(234,238,246,0.62)]">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => { window.location.href = '/login'; }}
            className="font-medium text-white underline-offset-4 hover:underline"
          >
            Sign in
          </button>
        </p>

        <p className="mt-8 text-center text-[11px] tracking-wide text-[rgba(234,238,246,0.30)]">
          Cadence is the product. Stratium is the company behind it.
        </p>
      </div>
    </div>
  );
}
